import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService, buildConvenientGlobalUserObject } from '../services/authService';
import { User as FirebaseUser } from 'firebase/auth';
import { setUser, useUser, setRooftop, setCustomerConfig, setCustomerConfigLoaded, setOrganizationConfig } from '@store';
import { toast } from 'react-toastify';
import { checkForPermittedRole } from '../services/authService';

export const useAuth = () => {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authAttempted, setAuthAttempted] = useState<string | boolean>(false);
  const authInProgress = useRef<string | null>(null);

  useEffect(() => {
    // Check for hash in URL on mount
    const params = new URLSearchParams(location.search);
    const hash = params.get('hash');
    const rooftopId = params.get('rooftopId');

    // Create a unique key for this hash attempt to prevent duplicate attempts
    const hashKey = hash ? `${location.pathname}:${hash}` : null;

    // Use ref for synchronous check to prevent race conditions
    if (hash && hashKey && authInProgress.current !== hashKey) {
      // Set ref immediately (synchronous, prevents duplicate calls)
      authInProgress.current = hashKey;
      setAuthAttempted(hashKey as any);

      // Authenticate with the hash
      authService.loginWithHash(hash, rooftopId)
        .then(async (result) => {
          console.log('Hash authentication successful:', result);
          // Build user object with rooftop ID
          const globalUser = await buildConvenientGlobalUserObject(result.rooftopId);

          // Check permissions
          const permittedRole = checkForPermittedRole(globalUser);
          if (!permittedRole) {
            toast.error(`You do not have access to this application. If you think this is an error, please contact a Hopdrive admin.`);
            authService.logout();
            return;
          }

          setUser(globalUser);
          setLoading(false);

          // Remove hash and rooftopId from URL for security
          params.delete('hash');
          params.delete('rooftopId');
          const newSearch = params.toString();
          navigate({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : '',
          }, { replace: true });
        })
        .catch((error) => {
          console.error('Hash authentication failed:', error);
          toast.error('Authentication failed. Please check your link and try again.');
          setLoading(false);
        });
    }

    // Set up Firebase auth state listener for session management
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser: FirebaseUser | null) => {
      // Skip if we're currently processing a hash login
      if (hash && !authAttempted) return;

      const isLoggingIn = !!(!user && firebaseUser);
      const isLoggingOut = !!(user && !firebaseUser);
      const isLoggedOut = !!(!user && !firebaseUser);

      if (isLoggingIn && !hash) {
        // Only auto-login for existing sessions, not hash-based logins
        const globalUser = await buildConvenientGlobalUserObject();

        // Check permissions
        const permittedRole = checkForPermittedRole(globalUser);
        if (!permittedRole) {
          toast.error(`You do not have access to this application. If you think this is an error, please contact a Hopdrive admin.`);
          authService.logout();
          return;
        }

        console.log(`(onAuthStateChanged) Auto-logging in using active session...`, {
          isLoggingIn,
          isLoggingOut,
          isLoggedOut,
          user,
          firebaseUser,
          globalUser,
        });
        setUser(globalUser);
        setLoading(false);
      }

      if (isLoggingOut) {
        console.log(`(onAuthStateChanged) Logging out...`, {
          isLoggingIn,
          isLoggingOut,
          isLoggedOut,
          user,
          firebaseUser,
        });

        // Clear localStorage and store to prevent data leakage between users
        localStorage?.clear();

        // Clear all store state
        setUser();
        setRooftop();
        setCustomerConfig();
        setCustomerConfigLoaded(false);
        setOrganizationConfig();
        setLoading(false);
      }

      if (isLoggedOut && !hash) {
        console.log('(onAuthStateChanged) Currently logged out.', {
          isLoggingIn,
          isLoggingOut,
          isLoggedOut,
          user,
          firebaseUser,
        });

        // Clear localStorage and store to prevent data leakage between users
        localStorage?.clear();

        // Clear all store state
        setUser();
        setRooftop();
        setCustomerConfig();
        setCustomerConfigLoaded(false);
        setOrganizationConfig();
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [location.search, authAttempted]);

  return { user, loading };
};
